import { useLocation, useNavigate } from 'react-router';
import { Icon } from './Icon';
import { mdHtml } from '../logik/md.js';
import { children, processTree, openDecisions, byId, statusOf, STATUS } from '../logik/model.js';
import { fmtDate } from '../logik/export/report.js';
import * as A from '../logik/actions.js';
import { Kundenlogo, KUNDE_HINWEIS } from './Settings';
import { Field } from './fields';
import { sync, darf } from '../sync';
import { menueOeffnen, adresse, kopieren, neuerTab } from './Kontextmenue';
import { bereinigen, type Zeile } from '../kontextmenue';
import { prozessLoeschen } from './Panel';
import '../styles/landkarte-filter.css';

const OHNE_STATUS = { id: 'ohne', name: 'Ohne Status', farbe: '#C9D3DA' };
/** Status eines Schritts wie im Cockpit: unbekannte und leere zählen als „ohne Status“. */
const schrittStatus = (n: any): string => (statusOf(n.status) ? n.status : 'ohne');

/** Kontextmenü einer Prozesskachel: öffnen, neuer Tab, Link, Status, Löschen (nur im Bearbeitungsmodus). */
function prozessMenue(data: any, p: any, edit: boolean, onOpen: (pid: string) => void): Zeile[] {
  const url = adresse(`/p/${encodeURIComponent(sync.projektId)}/prozess/${encodeURIComponent(p.id)}`);
  return bereinigen([
    { text: 'Öffnen', icon: 'eye', aktion: () => onOpen(p.id), testid: 'km-oeffnen' },
    { text: 'In neuem Tab öffnen', icon: 'extern', aktion: () => neuerTab(url) },
    { text: 'Link kopieren', icon: 'link', aktion: () => kopieren(url) },
    '-',
    {
      text: 'Status setzen', icon: 'target', wenn: darf('verantwortlich'), testid: 'km-status',
      unter: [
        { text: 'Kein Status', aktion: () => A.setProcessField(p.id, 'status', null), gewaehlt: !p.status, testid: 'km-status-keiner' },
        ...STATUS.map((x: any) => ({ text: x.name, aktion: () => A.setProcessField(p.id, 'status', x.id), gewaehlt: p.status === x.id, testid: 'km-status-' + x.id })),
      ],
    },
    { text: 'Unterprozess anlegen', icon: 'layers', aktion: () => onOpen(A.addProcess(p.id)), wenn: edit },
    '-',
    { text: 'Löschen', icon: 'trash', gefahr: true, aktion: () => prozessLoeschen(data, p), wenn: edit },
  ]);
}

interface LandkarteProps {
  data: any;
  edit: boolean;
  onOpen: (pid: string) => void;
  onDecisions: () => void;
  onHistory?: () => void;
  /** Reiter „Allgemeines“ (Titel, Einleitung, Kennzahlen) bzw. „Prozesse“ (nur die Kacheln); ohne Angabe beides */
  teil?: 'allgemein' | 'prozesse';
}

export function Landkarte({ data, edit, onOpen, onDecisions, onHistory, teil }: LandkarteProps) {
  const roots = children(data, null);
  const open = openDecisions(data);
  const stats = (pid: string) => {
    const ids = new Set(processTree(data, pid).map((p: any) => p.id));
    return {
      steps: data.nodes.filter((n: any) => ids.has(n.processId)).length,
      subs: ids.size - 1,
      dec: data.decisions.filter((d: any) => ids.has(d.processId) && d.status !== 'entschieden').length,
    };
  };
  const laneCount = data.lanes.length;
  // Filter aus dem Cockpit (?status=…): nur Prozesse, die Schritte mit diesem Status enthalten (Start und Ende zählen nicht)
  const { search, pathname } = useLocation();
  const nav = useNavigate();
  const gewaehlt = teil === 'allgemein' ? null : new URLSearchParams(search).get('status');
  const filter = gewaehlt ? [...STATUS, OHNE_STATUS].find((x: any) => x.id === gewaehlt) ?? null : null;
  const zaehlt = (n: any) => n.typ !== 'start' && n.typ !== 'ende';
  const treffer = (pid: string) => {
    if (!filter) return 0;
    const ids = new Set(processTree(data, pid).map((p: any) => p.id));
    return data.nodes.filter((n: any) => ids.has(n.processId) && zaehlt(n) && schrittStatus(n) === filter.id).length;
  };
  const filtern = (id: string | null) => nav(pathname + (id ? '?status=' + encodeURIComponent(id) : ''), { replace: true });
  const jeStatus = (id: string) => data.nodes.filter((n: any) => zaehlt(n) && schrittStatus(n) === id).length;
  const kacheln = roots.map((p: any, i: number) => ({ p, i })).filter(({ p }: any) => !filter || treffer(p.id) > 0);
  return (
    <div className={'landkarte' + (teil ? ' nur-' + teil : '')} data-testid={teil === 'allgemein' ? 'allgemeines' : 'landkarte'}>
      {teil !== 'prozesse' && <div className="head">
        <div>
          {!edit && (
            <>
              <h1>{data.meta.titel}</h1>
              <div className="sub">{data.meta.kunde}{data.meta.untertitel ? ' · ' + data.meta.untertitel : ''}</div>
              {data.meta.beschreibung && <div className="md" dangerouslySetInnerHTML={{ __html: mdHtml(data.meta.beschreibung) }} />}
            </>
          )}
          {edit && (
            <div className="lk-edit" data-testid="landkarte-edit">
              <Field label="Titel" value={data.meta.titel} onCommit={(v) => A.setMeta('titel', v)} testid="meta-titel" />
              <div className="grid2">
                <Field label="Kunde" value={data.meta.kunde} onCommit={() => {}} disabled hint={KUNDE_HINWEIS} testid="meta-kunde" />
                <Field label="Untertitel" value={data.meta.untertitel} onCommit={(v) => A.setMeta('untertitel', v)} />
              </div>
              <Field label="Einleitung (Markdown)" value={data.meta.beschreibung} onCommit={(v) => A.setMeta('beschreibung', v)} multiline tall />
              <div className="field"><span>Kundenlogo</span><Kundenlogo data={data} /></div>
            </div>
          )}
        </div>
        <div className="facts">
          <div className="fgrid">
            <div><div className="num">{data.processes.length}</div><div className="lab">Prozesse</div></div>
            <div><div className="num">{data.nodes.length}</div><div className="lab">Prozessschritte</div></div>
            <div><div className="num">{laneCount}</div><div className="lab">Beteiligte (Bahnen)</div></div>
            <div><button className="btn" style={{ height: 'auto', padding: '4px 8px', border: 0, background: 'none' }} onClick={onDecisions}><div><div className="num" style={{ color: 'var(--pa-decision)' }}>{open.length}</div><div className="lab">offene Entscheidungen ›</div></div></button></div>
          </div>
          <div className="hint" style={{ marginTop: '10px' }}><a href="#" onClick={(e) => { e.preventDefault(); onHistory?.(); }} title="Versionsverlauf anzeigen" data-testid="version-link">Version {data.meta.version || '–'}</a> · Stand {fmtDate(data.meta.stand)}{data.meta.geaendertVon ? ` · zuletzt gespeichert von ${data.meta.geaendertVon}${data.meta.geaendertAm ? ' am ' + new Date(data.meta.geaendertAm).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : ''}` : ''}</div>
        </div>
      </div>}
      {teil !== 'allgemein' && filter && <div className="lk-filter" data-testid="landkarte-filter">
        <span>Prozesse mit Schritten im Status</span>
        {[...STATUS, OHNE_STATUS].filter((x: any) => x.id === filter.id || jeStatus(x.id) > 0).map((x: any) => (
          <button key={x.id} className={'chip' + (x.id === filter.id ? ' aktiv' : '')} onClick={() => filtern(x.id)} data-testid={'filter-status-' + x.id}>
            <i style={{ background: x.farbe }} />{x.name} {jeStatus(x.id)}
          </button>
        ))}
        <button className="btn small" onClick={() => filtern(null)} data-testid="filter-aufheben"><Icon name="x" /> Filter aufheben</button>
      </div>}
      {teil !== 'allgemein' && filter && kacheln.length === 0 && <div className="empty-state">Kein Prozess enthält Schritte im Status „{filter.name}“.</div>}
      {teil !== 'allgemein' && <div className="tiles">
        {kacheln.map(({ p, i }: any) => {
          const s = stats(p.id);
          const subs = children(data, p.id).filter((c: any) => !filter || treffer(c.id) > 0);
          const wide = i === 0 && subs.length === 0 && s.steps > 0 && /überblick|gesamt/i.test(p.titel);
          const st = statusOf(p.status);
          return (
            <div key={p.id} className={'tile' + (wide ? ' wide' : '')} tabIndex={0} role="button" data-testid={'tile-' + p.id}
              onClick={() => onOpen(p.id)} onKeyDown={(e) => e.key === 'Enter' && onOpen(p.id)}
              onContextMenu={(e) => {
                // Unterprozess in der Kachel: dessen Menü
                const sub = (e.target as Element).closest('[data-sub]')?.getAttribute('data-sub');
                const ziel = sub ? byId(data.processes, sub) : p;
                if (ziel) menueOeffnen(e, prozessMenue(data, ziel, edit, onOpen));
              }}>
              <div className="bar" style={{ background: wide ? 'var(--pa-accent)' : laneColor(data, p) }} />
              <div>
                <div className="nr">{wide ? `${i + 1} · Überblick` : 'Prozess ' + (i + 1)}</div>
                <h3>{p.titel}</h3>
                {p.kurz && <div className="md kurz" dangerouslySetInnerHTML={{ __html: mdHtml(p.kurz) }} />}
                {subs.length > 0 && <ul>{subs.map((c: any) => <li key={c.id}><button data-sub={c.id} onClick={(e) => { e.stopPropagation(); onOpen(c.id); }}>{c.titel}{filter ? ` (${treffer(c.id)})` : ''}</button></li>)}</ul>}
                <div className="stats">
                  {filter && <span className="status-pill" data-testid={'treffer-' + p.id}><i style={{ background: filter.farbe }} />{treffer(p.id)} × {filter.name}</span>}
                  <span>{s.steps} Schritte</span>
                  {s.subs > 0 && <span>{s.subs} Unterprozesse</span>}
                  {s.dec > 0 && <span className="dec">{s.dec} offene Entscheidung{s.dec > 1 ? 'en' : ''}</span>}
                  {st && <span className="status-pill"><i style={{ background: st.farbe }} />{st.name}</span>}
                </div>
              </div>
              {wide && <button className="btn primary" onClick={(e) => { e.stopPropagation(); onOpen(p.id); }}>Gesamtablauf öffnen <Icon name="chevron" /></button>}
            </div>
          );
        })}
        {edit && !filter && (
          <button className="tile" style={{ borderStyle: 'dashed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '120px' }} onClick={() => onOpen(A.addProcess(null))}>
            <Icon name="plus" /> Hauptprozess anlegen
          </button>
        )}
      </div>}
    </div>
  );
}

function laneColor(data: any, p: any) {
  const counts = new Map<string, number>();
  for (const n of data.nodes) if (n.processId === p.id) counts.set(n.lane, (counts.get(n.lane) || 0) + 1);
  let best = p.laneIds[0], max = -1;
  for (const [l, c] of counts) if (c > max) { max = c; best = l; }
  return byId(data.lanes, best)?.farbe || 'var(--pa-accent)';
}
